import { forwardRef } from 'react';
import {
  ListItemText as MuiListItemText,
  ListItemTextProps as MuiListItemTextProps,
} from '@mui/material';

/**
 * ListItemText component - Text content for list items.
 *
 * This is a thin wrapper around Material-UI's ListItemText component that provides:
 * - Primary and secondary text rendering
 * - Typography component integration
 * - Text overflow handling
 * - Theme integration
 * - Flexible content (strings or React nodes)
 *
 * Common use cases:
 * - Displaying primary and secondary text in list items
 * - Rendering titles and descriptions
 * - Work item titles and metadata
 * - Navigation labels
 *
 * @example
 * // Simple primary text
 * <ListItemText primary="Primary text" />
 *
 * @example
 * // With secondary text
 * <ListItemText primary="Title" secondary="Description" />
 *
 * @example
 * // With custom Typography components
 * <ListItemText
 *   primary={<Typography variant="h6">Custom Title</Typography>}
 *   secondary={<Typography variant="body2">Custom description</Typography>}
 * />
 */
export type ListItemTextProps = MuiListItemTextProps;

export const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>((props, ref) => (
  <MuiListItemText ref={ref} {...props} />
));

ListItemText.displayName = 'ListItemText';
